import React, { useEffect, useState } from 'react';
import api from '../api';
import Modal from '../components/Modal';
import {
  ActionBar,
  Button,
  FormGrid,
  FormRow,
  Input,
  Label,
  Page,
  PageActions,
  PageHeader,
  PageHeaderMain,
  Subtitle,
  Surface,
  SurfaceBody,
  Table,
  TableWrap,
  Title,
  Toast,
} from '../components/ui';

interface ConfigItem {
  _id: string;
  key: string;
  value: string;
  label: string;
  description: string;
}

const emptyForm = { key: '', value: '', label: '', description: '' };

const ConfigPage: React.FC = () => {
  const [configs, setConfigs] = useState<ConfigItem[]>([]);
  const [form, setForm] = useState(emptyForm);
  const [modalOpen, setModalOpen] = useState(false);
  const [toast, setToast] = useState('');

  const load = async () => {
    const { data } = await api.get('/config');
    setConfigs(data);
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setForm(emptyForm);
    setModalOpen(true);
  };

  const openEdit = (item: ConfigItem) => {
    setForm({ key: item.key, value: item.value, label: item.label || '', description: item.description || '' });
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setForm(emptyForm);
  };

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    await api.post('/config', form);
    closeModal();
    setToast('Configuración guardada');
    load();
  };

  const remove = async (key: string) => {
    if (!window.confirm(`¿Eliminar "${key}"?`)) return;
    await api.delete(`/config/${key}`);
    setToast('Configuración eliminada');
    load();
  };

  return (
    <Page>
      <PageHeader>
        <PageHeaderMain>
          <Title>Configuración global</Title>
          <Subtitle>Variables del bot y del sistema.</Subtitle>
        </PageHeaderMain>
        <PageActions>
          <Button type="button" onClick={openCreate}>+ Nueva clave</Button>
        </PageActions>
      </PageHeader>

      {toast && <Toast>{toast}</Toast>}

      <Surface>
        <SurfaceBody $flush>
          <TableWrap>
            <Table>
              <thead>
                <tr>
                  <th style={{ width: '22%' }}>Key</th>
                  <th style={{ width: '28%' }}>Valor</th>
                  <th>Label</th>
                  <th style={{ width: '16%', textAlign: 'right' }}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {configs.map((c) => (
                  <tr key={c._id}>
                    <td>{c.key}</td>
                    <td style={{ maxWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={c.value}>{c.value}</td>
                    <td>{c.label}</td>
                    <td style={{ textAlign: 'right' }}>
                      <ActionBar>
                        <Button $variant="ghost" $size="sm" type="button" onClick={() => openEdit(c)}>Editar</Button>
                        <Button $variant="danger" $size="sm" type="button" onClick={() => remove(c.key)}>×</Button>
                      </ActionBar>
                    </td>
                  </tr>
                ))}
                {!configs.length && (
                  <tr><td colSpan={4} style={{ textAlign: 'center', padding: '1.5rem' }}>Sin configuraciones</td></tr>
                )}
              </tbody>
            </Table>
          </TableWrap>
        </SurfaceBody>
      </Surface>

      <Modal
        open={modalOpen}
        title={form.key ? `Editar: ${form.key}` : 'Nueva configuración'}
        onClose={closeModal}
        footer={(
          <>
            <Button $variant="ghost" type="button" onClick={closeModal}>Cancelar</Button>
            <Button type="submit" form="config-form">Guardar</Button>
          </>
        )}
      >
        <form id="config-form" onSubmit={save}>
          <FormGrid>
            <FormRow style={{ gridColumn: '1 / -1' }}>
              <Label>Key</Label>
              <Input value={form.key} onChange={(e) => setForm({ ...form, key: e.target.value })} required />
            </FormRow>
            <FormRow style={{ gridColumn: '1 / -1' }}>
              <Label>Valor</Label>
              <Input value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} />
            </FormRow>
            <FormRow>
              <Label>Etiqueta</Label>
              <Input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} />
            </FormRow>
            <FormRow>
              <Label>Descripción</Label>
              <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </FormRow>
          </FormGrid>
        </form>
      </Modal>
    </Page>
  );
};

export default ConfigPage;
